import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { LanguageChip } from '@/features/studies/creation/components/goals/LanguageChip'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

function PrivacyToggle({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors',
        checked ? 'bg-cta' : 'bg-[hsl(var(--toggle-inactive))]',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 block h-5 w-5 rounded-full shadow-sm transition-all',
          checked
            ? 'left-[22px] bg-card-inset dark:bg-card'
            : 'left-0.5 bg-foreground/30',
        )}
      />
    </button>
  )
}

export interface AudienceDetailsCardProps {
  name: string
  onNameChange: (value: string) => void
  audienceType: '' | 'B2B' | 'B2C'
  sharingMode: 'organization' | 'private'
  onAudienceTypeChange: (value: '' | 'B2B' | 'B2C') => void
  onSharingModeChange: (value: 'organization' | 'private') => void
  language: string
  onLanguageChange: (value: string) => void
  className?: string
}

export function AudienceDetailsCard({
  name,
  onNameChange,
  audienceType,
  sharingMode,
  onAudienceTypeChange,
  onSharingModeChange,
  language,
  onLanguageChange,
  className,
}: AudienceDetailsCardProps) {
  const { t } = useTranslation('app')
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-6 rounded-2xl bg-card/50 dark:bg-card p-6 sm:p-7',
        className,
      )}
    >
      <p className="text-lg font-medium leading-[1.4] text-foreground">
        {t('audienceCreate.details')}
      </p>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col">
          <label
            htmlFor="audience-name"
            className="text-[14px] leading-[1.5] text-foreground"
          >
            {t('audienceCreate.name')}
          </label>
          <input
            id="audience-name"
            type="text"
            placeholder={t('audienceCreate.namePlaceholder')}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            maxLength={200}
            className="rounded-none border-b border-card-border bg-transparent py-2 text-xl leading-[1.5] text-foreground placeholder:text-muted-foreground outline-none transition-colors focus:border-input-focus"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-x-6">
          <div className="flex min-w-0 flex-col gap-2">
            <span className="text-[14px] leading-[1.5] text-foreground">
              {t('audienceCreate.businessModel')}
            </span>
          <div
            role="radiogroup"
            aria-label={t('audienceCreate.businessModelAria')}
            className="inline-flex w-fit items-center rounded bg-card-inset p-1 shadow-sm"
          >
            {(['B2B', 'B2C'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={audienceType === t}
                onClick={() => onAudienceTypeChange(t)}
                className={cn(
                  'rounded px-3 py-1 text-base font-medium leading-[1.2] tracking-tight transition-colors',
                  audienceType === t
                    ? 'bg-cta text-cta-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

          <div className="flex min-w-0 flex-col gap-2">
            <span className="text-[14px] leading-[1.5] text-foreground">
              {t('audienceCreate.makePrivate')}
            </span>
            <PrivacyToggle
              checked={sharingMode === 'private'}
              onChange={(checked) => onSharingModeChange(checked ? 'private' : 'organization')}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex items-center gap-1">
              <span className="text-[14px] leading-[1.5] text-foreground">
                {t('audienceCreate.language')}
              </span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={t('audienceCreate.languageInfoAria')}
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-left leading-relaxed">
                    {t('audienceCreate.languageInfo')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <LanguageChip
              language={language}
              onLanguageChange={onLanguageChange}
              className="self-start"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
